import React, { useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";


const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    consent: true,
    message: ""
  });
  const [loading, setLoading] = useState(false);

  // Graceful fallbacks for environment variables to ensure 100% mockup accuracy if config is empty or whitespace
  const companyPhone = (import.meta.env.VITE_COMPANY_PHONE || "").trim() ;
  const companyEmail = (import.meta.env.VITE_COMPANY_EMAIL || "").trim() ;
  const companyAddress = (import.meta.env.VITE_COMPANY_ADDRESS || "").trim() ;
  const companyName = (import.meta.env.VITE_COMPANY_NAME || "").trim();
  const companyMapUrl = (import.meta.env.VITE_COMPANY_ADDRESS_MAP_URL || "").trim() ;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Form Submission",
          sender_name: formData.fullName,
          sender_email: formData.email,
          consent: formData.consent ? "Accepted" : "Not Accepted",
          message: formData.message,
          phone: formData.phone,
          reply_to: formData.email
        })
      });
      const data = await response.json();
      if (data.success) {
        alert("Message sent successfully!");
        setFormData({ fullName: "", email: "", consent: true, phone: "", message: "" });
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white font-semibold placeholder:text-slate-500 focus:outline-none focus:border-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)] transition-all duration-200";

  return (
    <div className="min-h-screen w-full bg-white text-[var(--text-primary)] relative overflow-x-hidden font-sans selection:bg-[var(--primary)] selection:text-white">
      <div className="relative z-10 flex w-full flex-col items-center">
        <Navbar />

        {/* Contact Page Wrapper */}
        <section className="max-w-5xl mx-auto w-full px-6 py-12 md:py-16 mt-20 flex flex-col gap-12 text-center">
          
          {/* Header */}
          <div className="flex flex-col items-center gap-3">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[var(--primary)]/30 bg-white/60 shadow-sm text-[10px] font-black tracking-widest text-[var(--primary)] uppercase leading-none">
              <span className="w-2 h-2 rounded-full bg-[var(--primary)] animate-pulse" />
              <span>Support is just a message away</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[54px] font-black text-[#0F172A] leading-tight tracking-tight mt-4 leading-none">
              Get in <span className="text-[var(--primary)]">Touch</span>
            </h1>

            <p className="text-slate-500 font-medium text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
              Have questions about your salary advance? Our team is here to provide quick and personal support.
            </p>
          </div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start w-full text-left">
            
            {/* Left Column: Contact details & Map (5 cols) */}
            <div className="lg:col-span-5 flex flex-col gap-6 w-full">
              
              {/* Info Card */}
              <div className="bg-white border border-slate-100/80 rounded-[20px] p-6 sm:p-8 shadow-[0_10px_25px_rgba(0,0,0,0.02)] flex flex-col gap-6 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[4px] bg-[var(--primary)]" />
                
                <p className="text-[#0F172A] font-black text-lg sm:text-xl leading-snug">
                  Your financial peace of mind is our top priority.
                </p>

                <div className="flex flex-col gap-5">
                  {[
                    { emoji: "📞", label: "Support Line", value: companyPhone, href: `tel:${companyPhone}` },
                    { emoji: "📧", label: "Official Email", value: companyEmail, href: `mailto:${companyEmail}` },
                    { emoji: "📍", label: "Headquarters", value: companyAddress, href: undefined }
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#F8FAFC] border border-slate-100 flex items-center justify-center text-lg shrink-0 text-[var(--primary)]">
                        {item.emoji}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 leading-none">
                          {item.label}
                        </span>
                        {item.href ? (
                          <a 
                            href={item.href} 
                            className="text-[#1E293B] font-extrabold hover:text-[var(--primary)] transition-colors text-sm sm:text-[15px] leading-snug"
                          >
                            {item.value}
                          </a>
                        ) : (
                          <span className="text-[#1E293B] font-extrabold text-sm sm:text-[15px] leading-snug">
                            {item.value}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Google Map */}
              <div className="rounded-[20px] overflow-hidden border border-slate-100/80 shadow-md">
                <iframe
                  title={companyName}
                  src={companyMapUrl}
                  width="100%"
                  height="300"
                  className="border-0 block"
                  allowFullScreen
                  loading="lazy"
                />
              </div>

            </div>

            {/* Right Column: Contact Form (7 cols) */}
            <div className="lg:col-span-7 w-full">
              <form
                onSubmit={handleSubmit}
                className="bg-[#0B1220] rounded-[24px] p-6 sm:p-8 md:p-10 border border-white/[0.04] shadow-[0_10px_25px_rgba(11,30,63,0.15)] flex flex-col gap-5 relative overflow-hidden"
              >
                {/* Background question mark watermark */}
                <div className="absolute right-[5px] bottom-[-25px] text-white/[0.03] text-[150px] font-black pointer-events-none select-none rotate-[8deg] z-0 leading-none">
                  ?
                </div>

                <div className="relative z-10 flex flex-col gap-1.5 mb-2">
                  <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                    Leave a Message
                  </h2>
                  <p className="text-slate-400 font-medium text-[14px]">
                    We'll get back to you as soon as possible.
                  </p>
                </div>

                <div className="relative z-10 flex flex-col gap-4">
                  <div className="flex flex-col gap-1.5 text-left w-full">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Name</label>
                    <input type="text" name="fullName" placeholder="Enter your full name" value={formData.fullName} onChange={handleChange} required className={inputClass} />
                  </div>

                  <div className="flex flex-col gap-1.5 text-left w-full">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                    <input type="email" name="email" placeholder="name@company.com" value={formData.email} onChange={handleChange} required className={inputClass} />
                  </div>

                  <div className="flex flex-col gap-1.5 text-left w-full">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Phone Number</label>
                    <input type="tel" name="phone" placeholder="+91 00000 00000" value={formData.phone} onChange={handleChange} required className={inputClass} />
                  </div>

                  <div className="flex flex-col gap-1.5 text-left w-full">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Message</label>
                    <textarea name="message" placeholder="How can we help you?" value={formData.message} onChange={handleChange} rows={4} className={inputClass} />
                  </div>
                </div>

                <div className="relative z-10 flex items-start gap-3 px-1 mt-1">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={formData.consent}
                    onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                    className="mt-1 h-5 w-5 accent-[var(--primary)] border-white/[0.1] bg-white/[0.03] rounded cursor-pointer shrink-0"
                  />
                  <label className="text-xs text-slate-400 font-medium leading-relaxed">
                    By submitting this form, you consent to receive communications regarding your inquiry via WhatsApp, email, or SMS.
                  </label>
                </div>

                <div className="relative z-10 mt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-white hover:bg-slate-50 text-[#0F172A] font-black text-[13px] sm:text-[14px] tracking-wider uppercase py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.01] active:scale-[0.99] shadow-md disabled:opacity-60"
                  >
                    <span>{loading ? "Sending..." : "Send Message"}</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>

              </form>
            </div>

          </div>

        </section>

        <Footer />
      </div>
    </div>
  );
};

export default ContactUs;
